import React from 'react';
import { Button, Modal } from 'antd';

import { ARROW_REPEAT, ARROW_RIGHT, ERASER, PLUS } from '../icons';
import { formatRelativeToNow } from '../../utils/formatters';
import { rowSideText } from './draftDiff';

// The word beside every row, so colour is never the only thing that says
// what happens to a field.
const KINDS = {
	fills: { word: 'Fills', Icon: PLUS },
	replaces: { word: 'Replaces', Icon: ARROW_REPEAT },
	clears: { word: 'Clears', Icon: ERASER },
};

const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "2 of your values are replaced. 3 empty fields are filled." */
export function replaceSummary(rows) {
	const losing = rows.filter(row => row.kind !== 'fills').length;
	const filling = rows.length - losing;
	return [
		losing ? `${count(losing, 'value', 'values')} you entered ${losing === 1 ? 'is' : 'are'} replaced.` : null,
		filling ? `${count(filling, 'empty field is', 'empty fields are')} filled.` : null,
	].filter(Boolean).join(' ');
}

/**
 * "Replace what is in the form?" — asked when a draft is applied over a
 * new-item form that holds something of the person's (the owner's live
 * pass: "this modal should visually illustrate what's happening"). One row
 * per field the change touches: the field, what the form holds now, what
 * the draft brings. A green row (`--success`) fills an empty field and
 * costs nothing; a red row (`--danger`) replaces or clears what the person
 * entered. Each row also says so in a word and an icon.
 *
 * Replace (primary, red: it drops what the form holds) applies the draft
 * without keeping anything. "Save mine as a draft" first keeps what the form
 * holds in the person's drafts, then applies. Cancel, Esc, ✕ and the mask
 * leave the form as it is.
 */
export default function DraftReplaceModal({
	open,
	draft,
	rows = [],
	resolveLookupValue,
	busy = null,
	onReplace,
	onKeepMine,
	onCancel,
}) {
	const title = String(draft?.title || '').trim() || 'Untitled draft';
	const age = formatRelativeToNow(draft?.updatedAt);
	const options = { resolveLookupValue };
	const working = Boolean(busy);

	return (
		<Modal
			open={open}
			title="Replace what is in the form?"
			onCancel={working ? undefined : onCancel}
			maskClosable={!working}
			keyboard={!working}
			destroyOnHidden
			width={640}
			rootClassName="draft-replace-modal"
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={working}>Cancel</Button>,
				<Button key="keep" onClick={onKeepMine} loading={busy === 'keep'} disabled={working && busy !== 'keep'}>
					Save mine as a draft
				</Button>,
				<Button key="replace" type="primary" danger onClick={onReplace} loading={busy === 'replace'} disabled={working && busy !== 'replace'}>
					Replace
				</Button>,
			]}
		>
			<p className="draft-replace__lead">
				The form becomes the draft <strong>{title}</strong>{age ? `, saved ${age}` : ''}. {replaceSummary(rows)}
			</p>
			<ul className="draft-replace__list" aria-label="What changes in the form">
				{rows.map(row => {
					const { word, Icon } = KINDS[row.kind] || KINDS.replaces;
					const from = rowSideText(row, 'from', options);
					const to = rowSideText(row, 'to', options);
					return (
						<li key={row.key} className={`draft-replace__row draft-replace__row--${row.kind}`} data-kind={row.kind}>
							<span className="draft-replace__kind">
								<Icon aria-hidden="true" />
								{word}
							</span>
							<span className="draft-replace__label">{row.label}</span>
							<span className="draft-replace__values">
								{row.kind === 'fills' ? (
									<span className="draft-replace__to">{to}</span>
								) : (
									<>
										<span className="draft-replace__from">{from}</span>
										<ARROW_RIGHT className="draft-replace__arrow" aria-hidden="true" />
										<span className="draft-replace__sr"> becomes </span>
										<span className={`draft-replace__to${to ? '' : ' draft-replace__to--empty'}`}>{to || 'Empty'}</span>
									</>
								)}
							</span>
						</li>
					);
				})}
			</ul>
			<p className="draft-replace__hint">Save mine as a draft keeps what the form holds now in your drafts.</p>
		</Modal>
	);
}
